import React, { useState } from 'react';
import { WebsiteNavbar } from './WebsiteNavbar';
import { WebsiteFooter } from './WebsiteFooter';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Send, 
  Calendar,
  CheckCircle,
  Clock,
  Users
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WebsiteContact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: 'demo',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        setError('Une erreur est survenue. Veuillez réessayer.');
      }
    } catch (err) {
      setError('Impossible d\'envoyer le message. Veuillez nous contacter par téléphone.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      label: 'Téléphone',
      value: '+33 7 55 60 85 50',
      href: 'tel:+33755608550'
    },
    {
      icon: Mail,
      label: 'Email',
      value: 'contact@syndicappli.fr',
      href: 'mailto:contact@syndicappli.fr'
    },
    {
      icon: MapPin,
      label: 'Adresse',
      value: '6 rue Léonard De Vinci, CS 20119, 53000 Laval',
      href: 'https://maps.google.com/?q=6+rue+Léonard+De+Vinci+Laval'
    }
  ];

  const benefits = [
    { icon: Calendar, text: 'Démonstration personnalisée de 30 min' },
    { icon: Users, text: 'Avec un expert de votre secteur' },
    { icon: Clock, text: 'Réponse sous 24h garantie' }
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-white" data-testid="website-contact">
        <WebsiteNavbar />
        <section className="pt-32 pb-20 min-h-[80vh] flex items-center justify-center bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700">
          <div className="max-w-2xl mx-auto px-6 text-center text-white">
            <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-8">
              <CheckCircle className="w-10 h-10" />
            </div>
            <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Message envoyé !
            </h1>
            <p className="text-xl text-white/80 mb-8">
              Merci pour votre intérêt. Notre équipe vous contactera dans les plus brefs délais.
            </p>
            <a
              href="/site"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-emerald-600 rounded-full font-semibold hover:shadow-xl transition-all"
            >
              Retour à l'accueil
            </a>
          </div>
        </section>
        <WebsiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="website-contact">
      <WebsiteNavbar />

      {/* Hero */}
      <section className="pt-32 pb-12 bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Contactez-nous
          </h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto">
            Une question ? Besoin d'une démonstration ? Notre équipe est à votre disposition.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Demandez une démo gratuite
              </h2>
              <p className="text-slate-600 mb-8">
                Remplissez le formulaire et nous vous recontacterons rapidement.
              </p>

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Nom complet *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      data-testid="contact-name-input"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none"
                      placeholder="Jean Dupont"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Email professionnel *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      data-testid="contact-email-input"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none"
                      placeholder="jean@votrecabinet.fr"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      data-testid="contact-phone-input"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none"
                      placeholder="06 12 34 56 78"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Nom du cabinet
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      data-testid="contact-company-input"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none"
                      placeholder="Cabinet Immobilier XYZ"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Objet de votre demande
                  </label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    data-testid="contact-subject-select"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none bg-white"
                  >
                    <option value="demo">Demande de démonstration</option>
                    <option value="pricing">Question sur les tarifs</option>
                    <option value="support">Support technique</option>
                    <option value="partnership">Partenariat</option>
                    <option value="other">Autre</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Votre message
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    data-testid="contact-message-textarea"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none resize-none"
                    placeholder="Décrivez votre besoin ou posez vos questions..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="contact-submit-btn"
                  className="w-full py-4 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-emerald-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Envoyer ma demande
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div>
              {/* Benefits */}
              <div className="bg-emerald-50 rounded-3xl p-8 mb-8">
                <h3 className="text-xl font-bold text-slate-900 mb-6">Ce que vous obtenez</h3>
                <ul className="space-y-4">
                  {benefits.map((benefit, index) => (
                    <li key={index} className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <benefit.icon className="w-5 h-5 text-emerald-600" />
                      </div>
                      <span className="text-slate-700">{benefit.text}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Contact Details */}
              <div className="bg-white rounded-3xl shadow-lg p-8 mb-8">
                <h3 className="text-xl font-bold text-slate-900 mb-6">Nos coordonnées</h3>
                <ul className="space-y-6">
                  {contactInfo.map((info, index) => (
                    <li key={index}>
                      <a
                        href={info.href}
                        target={info.href.startsWith('http') ? '_blank' : undefined}
                        rel={info.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                        className="flex items-start gap-4 group"
                      >
                        <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-100 transition-colors">
                          <info.icon className="w-6 h-6 text-slate-600 group-hover:text-emerald-600 transition-colors" />
                        </div>
                        <div>
                          <div className="text-sm text-slate-500 mb-1">{info.label}</div>
                          <div className="text-slate-900 font-medium group-hover:text-emerald-600 transition-colors">
                            {info.value}
                          </div>
                        </div>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Calendly Widget */}
              <div className="bg-slate-900 rounded-3xl p-8 text-white">
                <h3 className="text-xl font-bold mb-4">Réservez directement un créneau</h3>
                <p className="text-slate-400 mb-6">
                  Choisissez le moment qui vous convient pour une démonstration personnalisée.
                </p>
                <a
                  href="https://calendly.com/syndicappli/demo"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="calendly-link"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-500 rounded-xl font-semibold hover:bg-emerald-600 transition-colors"
                >
                  <Calendar className="w-5 h-5" />
                  Ouvrir Calendly
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="h-96 bg-slate-200">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2685.5!2d-0.77!3d48.07!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDjCsDA0JzEyLjAiTiAwwrA0NicxMi4wIlc!5e0!3m2!1sfr!2sfr!4v1234567890"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Localisation SyndicAppli"
        ></iframe>
      </section>

      <WebsiteFooter />
    </div>
  );
};

export { WebsiteContact };
